
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Validate } from '@/types';
import { decodeSuiteOwner } from '@/utils/decoding';
import { formatPhoneNumber } from '@/utils/format';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextField from '@/components/shared/TextField.vue';
import '@/validation-rules';

const namespace: string = 'suiteOwners';

@Component<SuiteOwnerModal>({
	components: {
		TextField,
		StandardModal,
		ValidationObserver,
		ValidationProvider
	}
})
export default class SuiteOwnerModal extends Vue {
	@Action('addSuiteOwner', { namespace }) private addSuiteOwner!: (suiteOwner: EditableSuiteOwner) => Promise<SuiteOwner | void>;
	@Action('updateSuiteOwner', { namespace }) private updateSuiteOwner!: (suiteOwner: EditableSuiteOwner) => Promise<void>;
	@Getter('getSuiteOwnerById', { namespace }) private getSuiteOwner!: (ownerId: number) => SuiteOwner;
	@Prop({ type: Number, required: false }) private ownerId!: number|undefined;
	@Prop({ type: Boolean, required: true, default: '' }) private showModal!: boolean;

	$refs!: { observer: Validate; };

	private isLoading: boolean = false;
	private mutableSuiteOwner: EditableSuiteOwner = {
		firstName: '',
		lastName: '',
		company_name: '',
		title: '',
		email: '',
		phone_number: ''
	};

	private get modalTitle(): string {
		return this.ownerId
			? this.$t('suites.event_settings.suite_owners.modal.edit_title')
			: this.$t('suites.event_settings.suite_owners.modal.new_title');
	}

	private get suiteOwner(): SuiteOwner | null {
		return this.ownerId ? this.getSuiteOwner(this.ownerId) : null;
	}

	/**
	 * Set mutable suite owner to the selected suite owner if applicable
	 *
	 * @return {void}
	 */
	private mounted(): void {
		if (this.suiteOwner) {
			this.mutableSuiteOwner = decodeSuiteOwner(this.suiteOwner);
		}
	}

	/**
	* Format the phone number - remove non-digital characters
	* and add dashes to the respective positions
	*
	* @return {void}
	*/
	private formatPhoneNumber(): void {
		this.mutableSuiteOwner.phone_number = formatPhoneNumber(this.mutableSuiteOwner.phone_number);
	}

	/**
	 * Check if inputs are valid before saving or editing suite owner
	 *
	 * @return {Promise<void>}
	 */
	private async submitHandler(): Promise<void> {
		const isValid = await this.$refs.observer.validate();

		if (isValid) {
			this.ownerId ? this.editSuiteOwner() : this.saveSuiteOwner();
		}
	}

	/**
	 * Create new suite owner
	 *
	 * @return {Promise<void>}
	 */
	private async saveSuiteOwner(): Promise<void> {
		this.isLoading = true;
		try {
			const newSuiteOwner = await this.addSuiteOwner(this.mutableSuiteOwner);
			this.$emit('suite-owner-created', newSuiteOwner);
			this.$emit('close');
		}
		catch (errorMessage) {
			Toast.error(this.$t('suites.event_settings.suite_owners.modal.error_creating', { errorMessage }));
			this.isLoading = false;
		}
	}

	/**
	 * Edit suite owner
	 *
	 * @return {Promise<void>}
	 */
	private async editSuiteOwner(): Promise<void> {
		this.isLoading = true;
		try {
			await this.updateSuiteOwner(this.mutableSuiteOwner);
			this.$emit('close');
		}
		catch (errorMessage) {
			Toast.error(this.$t('suites.event_settings.suite_owners.modal.error_updating', { errorMessage }));
			this.isLoading = false;
		}
	}

	/**
	 * Emit event to close modal
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		this.$emit('close');
	}
}
